import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  Checkbox,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import {
  ChangeEvent,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch } from "../../app/hook";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import {
  useGetAuthQuery,
  useGetDirApplicantCompaniesQuery,
  usePostStoreDirectApplicationsMutation,
  usePostStoreDirectApplicationsCountMutation,
  usePostStoreDirectApplicationsAllMutation,
  usePostCompanyCodeCheckMutation,
} from "../../features/api";
import { getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { downloadBase64File } from "../../utils/downLoad";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import {
  COMMON_LABELS,
  COMMON_CONFIGS,
  shopReceiveUnitMethodPattern,
} from "../default-constants";
import { CONFIGS, LABELS, CHECK_TARGET_DIRECT_STORE } from "./constants";
import ErrorTable, {
  getMessageItemList,
  MessageItem,
} from "../UI/table/ErrorTable";
import CompanyCodeErrorTable, {
  ErrorMessageItem,
  CheckResult,
  getErrorMessageItemList,
} from "../UI/table/CompanyCodeErrorTable";
import {
  getApplicantCompanyItems,
  getContentMessage,
  getShopReceiveUnitMethod,
  getShopReceiveUnitMethodLabel,
} from "./util";
import { PostStoreDirectApplicationsResponse } from "../../features/api-interfaces";
import MultilineText from "../UI/MultilineText";

/**
 * 店舗情報申請（直接）画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const isApply = !!useHasAuthorityHook(PERMISSIONS.APPLY);
  const isMasterApproval = !!useHasAuthorityHook(PERMISSIONS.MASTER_APPROVAL);
  const isDocomoMasterUser = isDocomoUser && isMasterApproval && isApply;
  const storeInputRef = useRef<HTMLInputElement>(null);
  const storeErrorRef = createRef<HTMLDivElement>();
  const [uploadFile, setUploadFile] = useState<File>();
  const [companyCodeErrorText, setCompanyCodeErrorText] = useState("");
  const [fileUploadErrorText, setFileUploadErrorText] = useState("");
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [maintenanceFlg, setMaintenanceFlg] = useState(false);
  const [applicantCompanyList, setApplicantCompanyList] = useState<
    SelectProps.Option[]
  >([]);
  const [selectedApplicantCompany, setSelectedApplicantCompany] =
    useState<SelectProps.Option>({
      label: "",
      value: "",
    });
  const [shopReceiveUnitMethod, setShopReceiveUnitMethod] = useState(
    Number(shopReceiveUnitMethodPattern.difference.value)
  );
  const [storeErrorMessageList, setStoreErrorMessageList] = useState<
    MessageItem[]
  >([]);
  const { companyCode } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }) => ({
      companyCode: data?.companyCode,
    }),
  });
  const { data: dirApplicantCompanies } = useGetDirApplicantCompaniesQuery();
  const [postStoreDirectApplications] =
    usePostStoreDirectApplicationsMutation();
  const [postStoreDirectApplicationsCount] =
    usePostStoreDirectApplicationsCountMutation();
  const [postStoreDirectApplicationsAll] =
    usePostStoreDirectApplicationsAllMutation();
  const [companyCodeCheck] = usePostCompanyCodeCheckMutation();
  const isDifferenceApplication =
    shopReceiveUnitMethod ===
    Number(shopReceiveUnitMethodPattern.difference.value);
  const isAllApplication =
    shopReceiveUnitMethod === Number(shopReceiveUnitMethodPattern.all.value);

  const scrollToBottomOfList = useCallback(() => {
    // 店舗用エラー内容にスクロール
    storeErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [storeErrorRef]);

  useEffect(() => {
    scrollToBottomOfList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeErrorMessageList]);

  useEffect(() => {
    setApplicantCompanyList(getApplicantCompanyItems(dirApplicantCompanies));
    if (!isDocomoUser && companyCode) {
      setShopReceiveUnitMethod(
        getShopReceiveUnitMethod(dirApplicantCompanies, companyCode)
      );
    }
  }, [dirApplicantCompanies, isDocomoUser, companyCode]);

  /**
   * 申請事業者のセレクトボックスハンドラ.
   * @param {SelectProps.Option} selectedOption 選択された申請事業者
   */
  const selectCompanyHandler = (selectedOption: SelectProps.Option) => {
    setSelectedApplicantCompany(selectedOption);
    setShopReceiveUnitMethod(
      getShopReceiveUnitMethod(dirApplicantCompanies, selectedOption.value!)
    );
    setCompanyCodeErrorText("");
  };

  /**
   * 店舗申請提出用ファイルを設定する.
   * @param {ChangeEvent<HTMLInputElement>} event
   */
  const onChangeStoreFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.[0]) {
      return;
    }
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      dispatch(
        addNotification({
          msgId: "KGRERR057",
          msgArgs: [],
        })
      );
      return;
    }
    setUploadFile(files[0]);
    setFileUploadErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  /**
   * 店舗申請アップロードボタンイベント.
   * @param {CustomEvent<ClickDetail>} event
   */
  const storeUploadOnClick = (event: CustomEvent<ClickDetail>) => {
    event.preventDefault();
    storeInputRef.current?.click();
  };

  /**
   * 申請ボタンを押下したときの処理
   */
  const onClickButton = async () => {
    if ([isRequiredError, isFileUploaded].some((check) => check())) {
      return;
    }

    if (maintenanceFlg) {
      // メンテナンス時はメンテナンス確認用のダイアログを表示
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.MAINTENANCE_NOTICE,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            confirmExecute();
          },
        })
      );
    } else {
      // それ以外は通常ダイアログ
      confirmExecute();
    }
  };

  /**
   * 申請事業者が選択されているかチェックする.
   * @returns {boolean} true: チェックNG
   */
  const isRequiredError = (): boolean => {
    if (isDocomoUser && !selectedApplicantCompany.value) {
      setCompanyCodeErrorText(
        getMessage("KGRERR023", [LABELS.APPLICANT_COMPANY]).label
      );
      return true;
    }
    return false;
  };

  /**
   * ファイルがアップロードされているかチェックする.
   * @returns {boolean} true: チェックNG
   */
  const isFileUploaded = (): boolean => {
    if (uploadFile === undefined) {
      setFileUploadErrorText(getMessage("KGRERR054").label);
      return true;
    }
    return false;
  };

  /**
   * ファイルアップロード実行確認
   */
  const confirmExecute = async () => {
    const result = await dataCheck();

    if (!result.isError) {
      return;
    }

    const fileData = new FormData();
    if ([isRequiredError, isFileUploaded].some((check) => check())) {
      return;
    }

    const applicantCompanyCode = isDocomoUser
      ? selectedApplicantCompany.value!
      : companyCode!;
    fileData.append(CONFIGS.APPLICANT_COMPANY_CODE, applicantCompanyCode);
    fileData.append(CONFIGS.FILE, uploadFile!);
    fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.STORE);
    fileData.append(CONFIGS.MAINTENANCE_FLG, maintenanceFlg ? "true" : "false");
    let closedStoreCount = 0;

    if (isAllApplication) {
      try {
        setIsSpinnerDisplay(true);
        setIsButtonDisabled(true);
        const response = await postStoreDirectApplicationsCount(
          fileData
        ).unwrap();
        if (COMMON_CONFIGS.API.STATUS_ERROR === response.status) {
          throw new Error();
        } else {
          closedStoreCount = response.closed;
        }
      } catch (error) {
        // エラー時は処理を終了
        return;
      } finally {
        setIsSpinnerDisplay(false);
        setIsButtonDisabled(false);
      }
    }
    showApplicationModal(fileData, closedStoreCount, result.itemList);
    // 同じファイルを連続アップロードできるように処理
    if (storeInputRef.current) {
      storeInputRef.current.value = "";
    }
  };

  /**
   * 確認ダイアログを表示する.
   * @param {FormData} fileData ファイル
   */
  const showApplicationModal = async (
    fileData: FormData,
    closedStoreCount: number,
    itemList: ErrorMessageItem[]
  ) => {
    let contentMessage = getContentMessage(
      isAllApplication,
      closedStoreCount,
      uploadFile!.name
    );

    setTimeout(() => {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: (
            <>
              <MultilineText content={contentMessage} />
              {itemList.length > 0 && (
                <>
                  <br />
                  <CompanyCodeErrorTable data={itemList} />
                </>
              )}
            </>
          ),
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            storeDirectApplication(fileData);
          },
        })
      );
    }, 0);
  };

  /**
   * 確認ダイアログでOKボタンを押したときの処理.
   * @param {FormData} fileData ファイル
   */
  const storeDirectApplication = async (fileData: FormData) => {
    setIsSpinnerDisplay(true);
    setIsButtonDisabled(true);
    setFileUploadErrorText("");

    if (isDifferenceApplication) {
      executeApi(postStoreDirectApplications(fileData));
    } else if (isAllApplication) {
      executeApi(postStoreDirectApplicationsAll(fileData));
    }
  };

  /**
   * APIを呼び出し、レスポンスを処理する.
   * @param apiCall APIを呼び出す関数
   */
  const executeApi = async (apiCall: any) => {
    try {
      const res = await apiCall.unwrap();
      handleApiResponse(res);
    } finally {
      setIsSpinnerDisplay(false);
      setIsButtonDisabled(false);
    }
  };

  /**
   * APIレスポンスを処理する.
   * @param {PostStoreDirectApplicationsResponse} response API戻り値
   */
  const handleApiResponse = (response: PostStoreDirectApplicationsResponse) => {
    setSelectedApplicantCompany({
      label: "",
      value: "",
    });
    setUploadFile(undefined);

    if (COMMON_CONFIGS.API.STATUS_ERROR === response.status) {
      if (
        response.errorMessageList !== null &&
        response.errorMessageList.length !== 0
      ) {
        setStoreErrorMessageList(getMessageItemList(response.errorMessageList));
      }
      if (response.item) {
        downloadBase64File(response.item);
      }
    } else {
      setStoreErrorMessageList([]);
    }
  };

  /**
   * 申請ボタン押下後の企業コードチェック
   */
  const dataCheck = async (): Promise<CheckResult> => {
    const result: CheckResult = { isError: true, itemList: [] };

    if (uploadFile === undefined) {
      return result;
    }

    const fileData = new FormData();

    fileData.append(CONFIGS.TARGET, CHECK_TARGET_DIRECT_STORE);
    fileData.append(CONFIGS.FILE, uploadFile);

    setIsSpinnerDisplay(true);
    setIsButtonDisabled(true);

    await companyCodeCheck(fileData)
      .unwrap()
      .then((res) => {
        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          result.isError = false;
        }
        if (res.errorInfos !== null) {
          result.itemList = getErrorMessageItemList(res.errorInfos);
        }
      })
      .finally(() => {
        setIsSpinnerDisplay(false);
        setIsButtonDisabled(false);
      });

    return result;
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container>
        <SpaceBetween direction="horizontal" size="xs">
          <Box variant="p">{LABELS.APPLICATION_TYPE_HEADER}</Box>
          <Box variant="h3">
            {getShopReceiveUnitMethodLabel(shopReceiveUnitMethod)}
          </Box>
          <Box variant="p">{LABELS.APPLICATION_TYPE_FOOTER}</Box>
        </SpaceBetween>
      </Container>
      <Container
        header={
          <Header variant="h2" description="">
            {LABELS.APPLICATION_FILE}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="xxl">
          <SpaceBetween direction="vertical" size="xxl">
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="l">
                  {isSpinnerDisplay && <Spinner size="big" />}
                  <Button
                    iconAlign="right"
                    onClick={onClickButton}
                    variant="primary"
                    disabled={isButtonDisabled}
                  >
                    {COMMON_LABELS.BTNS.APPLY}
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween direction="vertical" size="xxl">
                {isDocomoUser && (
                  <ColumnLayout columns={4}>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.APPLICANT_COMPANY}</Box>
                          <Box variant="small" color="text-status-error">
                            {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                          </Box>
                        </SpaceBetween>
                      }
                      errorText={companyCodeErrorText}
                    >
                      <Select
                        selectedAriaLabel="Selected"
                        filteringType="auto"
                        onChange={({ detail: { selectedOption } }) => {
                          selectCompanyHandler(selectedOption);
                        }}
                        selectedOption={selectedApplicantCompany}
                        options={applicantCompanyList}
                      />
                    </FormField>
                  </ColumnLayout>
                )}
                <FileUpload
                  inputRef={storeInputRef}
                  label={LABELS.FOR_STORE}
                  name={"corporateFile"}
                  placeholder={LABELS.FILE_PLACEHOLDER}
                  accept={CONFIGS.FILE_EXTENSION}
                  fileName={uploadFile ? uploadFile.name : ""}
                  setUploadFile={setUploadFile}
                  errorText={fileUploadErrorText}
                  setErrorText={setFileUploadErrorText}
                  onChange={onChangeStoreFile}
                  onUpload={storeUploadOnClick}
                />
                {isDocomoMasterUser && (
                  <Checkbox
                    checked={maintenanceFlg}
                    onChange={({ detail: { checked } }) => {
                      setMaintenanceFlg(checked);
                    }}
                  >
                    {LABELS.MAINTENANCE_LABEL}
                  </Checkbox>
                )}
              </SpaceBetween>
            </Form>
          </SpaceBetween>
          <SpaceBetween direction="vertical" size="xxs">
            {storeErrorMessageList.length !== 0 && (
              <>
                <ErrorTable
                  data={storeErrorMessageList}
                  title={LABELS.ERROR_TITLE_FOR_STORE}
                />
                <div id="storeError" ref={storeErrorRef} />
              </>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
